import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Brand from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { SignOut, House, Storefront, PaperPlaneTilt, UsersThree } from "@phosphor-icons/react";

/**
 * PartnerLayout — minimal shell for partner-facing pages.
 * Not the full AppLayout (partners don't need candidate kanban, jobs
 * CRUD, etc). Top bar + 3 tab links + sign out.
 */
export default function PartnerLayout({ children }) {
    const { user, setUser } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();

    const signOut = async () => {
        try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
        setUser(false);
        nav("/login", { replace: true });
    };

    const tabs = [
        { to: "/partner/dashboard", label: "Overview", icon: House },
        { to: "/partner/lead-pool", label: "Lead pool", icon: UsersThree },
        { to: "/partner/marketplace", label: "Open roles", icon: Storefront },
        { to: "/partner/submissions", label: "My submissions", icon: PaperPlaneTilt },
    ];

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="partner-shell">
            <header className="border-b border-[#E5E5E5] bg-white sticky top-0 z-20">
                <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 gap-3">
                    <Brand size="h-8" to="/partner/dashboard" data-testid="partner-brand" />
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-xs text-[#525252]">
                            <span className="text-[#A3A3A3]">Signed in as</span>{" "}
                            <span className="font-bold text-ink">{user?.name || user?.email}</span>
                        </span>
                        <button
                            type="button"
                            onClick={signOut}
                            data-testid="partner-signout"
                            className="text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#DC2626] inline-flex items-center gap-1"
                        >
                            <SignOut size={12} weight="bold" /> Sign out
                        </button>
                    </div>
                </div>
                <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-1 sm:gap-2 overflow-x-auto">
                    {tabs.map((t) => {
                        const active = loc.pathname === t.to || (t.to !== "/partner/dashboard" && loc.pathname.startsWith(t.to));
                        const Icon = t.icon;
                        return (
                            <Link
                                key={t.to}
                                to={t.to}
                                data-testid={`partner-tab-${t.to.split("/").pop()}`}
                                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                    active
                                        ? "border-[#0891B2] text-[#0A0A0A]"
                                        : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
                                }`}
                            >
                                <Icon size={14} weight="bold" /> {t.label}
                            </Link>
                        );
                    })}
                </nav>
            </header>
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                {children}
            </main>
        </div>
    );
}
